import { useState, useMemo } from 'react';
import { type UseFormSetError } from 'react-hook-form';
import {
  useCarRiderQueue,
  useCreateCarRiderQueueEntry,
  useUpdateCarRiderQueueEntry,
  useDeleteCarRiderQueueEntry,
  useCarRiderQueueEntryById,
} from '../../../hooks/queries/useVisitorManagement';
import { useSites } from '../../../hooks/queries/useSites';
import type {
  CarRiderQueueEntry,
  CarRiderQueueEntryWrite,
} from '../../../types/VisitorManagementTypes';
import { toaster } from '../../ui/toaster';
import { ApiValidationError } from '../../../lib/http-client';
import { StatusBadge, type Column, type StatusType } from '@themes/aegix';
import { useSearchParams } from 'react-router-dom';
import { formatLocalDateTime } from '../../../lib/date';

interface CarRiderRow {
  [key: string]: unknown;
  id: string;
  student_name: string;
  school_name: string;
  status: string;
  queued_at: string;
  notes: string;
}

export function useCarRiderQueueCrud() {
  const [searchParams] = useSearchParams();
  const urlId = searchParams.get('public_id');
  const urlTab = searchParams.get('subtab');
  const isCarRiderTab =
    urlTab?.toLowerCase().replace(/\s+/g, '-') === 'car-rider';
  const { data: entryById, error: entryByIdError } = useCarRiderQueueEntryById(
    urlId as string,
    isCarRiderTab,
  );
  const { data, isLoading, error } = useCarRiderQueue();
  const { data: sitesData } = useSites({ page_size: 100 });
  const createMutation = useCreateCarRiderQueueEntry();
  const updateMutation = useUpdateCarRiderQueueEntry();
  const deleteMutation = useDeleteCarRiderQueueEntry();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editItem, setEditItem] = useState<CarRiderQueueEntry | null>(null);

  const sitesMap = useMemo(() => {
    const map = new Map<number, string>();
    sitesData?.results?.forEach((site) => {
      map.set(site.id, site.name);
    });
    return map;
  }, [sitesData]);

  const isSingleView = Boolean(
    urlId && isCarRiderTab && entryById && !entryByIdError,
  );
  const entriesDetails =
    isSingleView && entryById ? [entryById] : (data ?? []);

  const rows: CarRiderRow[] = entriesDetails
    .filter((r): r is CarRiderQueueEntry => !!r)
    .map((r) => ({
      id: r.public_id,
      student_name: r.student_name || `Student #${r.student}`,
      school_name: sitesMap.get(r.school) || `Site #${r.school}`,
      status: r.status,
      queued_at: r.queued_at ? new Date(r.queued_at).toLocaleString() : '',
      notes: r.notes || '',
    }));

  const columns: Column<CarRiderRow>[] = [
    { key: 'student_name', label: 'Student', sortable: true, width: '180px' },
    { key: 'school_name', label: 'School', sortable: true, width: '150px' },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      render: (row) => {
        let badgeStatus: StatusType = 'warning' as StatusType;
        if (row.status === 'picked_up') badgeStatus = 'active' as StatusType;
        if (row.status === 'cancelled') badgeStatus = 'inactive' as StatusType;
        if (row.status === 'called') badgeStatus = 'info' as StatusType;
        return (
          <StatusBadge
            status={badgeStatus}
            label={row.status.toUpperCase().replace('_', ' ')}
          />
        );
      },
    },
    { key: 'queued_at', label: 'Queued At', sortable: true, width: '160px' },
    { key: 'notes', label: 'Notes', width: '200px' },
  ];

  const handleClose = () => {
    setDrawerOpen(false);
    setEditItem(null);
  };

  const handleRowClick = (row: CarRiderRow) => {
    const item = data?.find((r) => r.public_id === row.id);
    if (item) {
      setEditItem(item);
      setDrawerOpen(true);
    }
  };

  const handleSubmit = (
    values: CarRiderQueueEntryWrite,
    setError: UseFormSetError<CarRiderQueueEntryWrite>,
  ) => {
    const onError = (err: Error) => {
      if (err instanceof ApiValidationError) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          setError(field as keyof CarRiderQueueEntryWrite, {
            message: messages.join(', '),
          });
        }
      }
      toaster.create({
        title: 'Save failed',
        description: err.message,
        type: 'error',
      });
    };

    const submitValues = {
      ...values,
      queued_at: values.queued_at ? new Date(values.queued_at).toISOString() : new Date().toISOString(),
    };

    if (editItem) {
      updateMutation.mutate(
        { publicId: editItem.public_id, data: submitValues },
        {
          onSuccess: () => {
            toaster.create({ title: 'Car rider queue entry updated', type: 'success' });
            handleClose();
          },
          onError,
        },
      );
    } else {
      createMutation.mutate(submitValues, {
        onSuccess: () => {
          toaster.create({ title: 'Car rider queue entry created', type: 'success' });
          handleClose();
        },
        onError,
      });
    }
  };

  const handleDelete = () => {
    if (!editItem) return;
    deleteMutation.mutate(editItem.public_id, {
      onSuccess: () => {
        toaster.create({ title: 'Car rider queue entry deleted', type: 'success' });
        handleClose();
      },
      onError: (err) =>
        toaster.create({
          title: 'Delete failed',
          description: err.message,
          type: 'error',
        }),
    });
  };

  const initialValues: CarRiderQueueEntryWrite = useMemo(
    () =>
      editItem
        ? {
          student: editItem.student,
          school: editItem.school,
          status: editItem.status,
          queued_at: editItem.queued_at ? formatLocalDateTime(editItem.queued_at) : formatLocalDateTime(new Date()),
          notes: editItem.notes || '',
        }
        : {
          student: 0,
          school: 0,
          status: 'waiting',
          queued_at: formatLocalDateTime(new Date()),
          notes: '',
        },
    [editItem],
  );

  return {
    data: entriesDetails,
    isLoading,
    error,
    drawerOpen,
    setDrawerOpen,
    editItem,
    handleClose,
    handleSubmit,
    handleDelete,
    handleRowClick,
    initialValues,
    rows,
    columns,
    sitesData,
  };
}
